/**
 * PromoteLeadModal - convert a Lead into an Investor, Company, or Engagement.
 *
 * The form is pre-filled from the lead's contact + responses, then submitted
 * as a `mapping` override hash to POST /leads/{id}/promote. Backend will merge
 * those fields with its own defaults (so leaving a field blank doesn't blow
 * away what the backend would otherwise infer).
 */
import React, { useMemo, useState } from 'react';
import { Loader2, X, ArrowRight } from 'lucide-react';
import type { Lead, PromoteTargetKind } from '../types/lead';

interface Props {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onConfirm: (
    targetKind: PromoteTargetKind,
    mapping: Record<string, any>,
  ) => Promise<void> | void;
}

const TARGET_OPTIONS: Array<{ value: PromoteTargetKind; label: string; helper: string }> = [
  {
    value: 'investor',
    label: 'Investor',
    helper: 'Add to the investor intelligence library.',
  },
  {
    value: 'company',
    label: 'Company (Startup)',
    helper: 'Create a company profile for analysis.',
  },
  {
    value: 'engagement',
    label: 'Engagement',
    helper: 'Open a new sales engagement pipeline.',
  },
];

/** Build a one-line summary of every captured Q/A for engagement notes. */
function summarizeResponses(lead: Lead): string {
  const lines: string[] = [];
  if (lead.persona) lines.push(`Persona: ${lead.persona}`);
  lines.push(`Score: ${lead.total_score} (${lead.tier})`);
  if (lead.captured_responses && lead.captured_responses.length > 0) {
    lines.push('');
    lines.push('Responses:');
    for (const r of lead.captured_responses) {
      const ans = Array.isArray(r.answer)
        ? r.answer.join(', ')
        : typeof r.answer === 'object' && r.answer !== null
        ? JSON.stringify(r.answer)
        : String(r.answer ?? '');
      lines.push(`- ${r.question_text || r.question_id}: ${ans}`);
    }
  }
  if (lead.notes) {
    lines.push('');
    lines.push(`Existing notes: ${lead.notes}`);
  }
  return lines.join('\n');
}

const PromoteLeadModal: React.FC<Props> = ({ open, lead, onClose, onConfirm }) => {
  const [targetKind, setTargetKind] = useState<PromoteTargetKind>(() => {
    if (lead.persona === 'investor') return 'investor';
    if (lead.persona === 'startup') return 'company';
    return 'engagement';
  });

  // Investor pre-fill
  const [investorFullName, setInvestorFullName] = useState<string>(
    lead.contact_name || '',
  );
  const [investorFirmName, setInvestorFirmName] = useState<string>(
    lead.company_name || '',
  );
  const [investorEmail, setInvestorEmail] = useState<string>(
    lead.contact_email || '',
  );
  const [investorLinkedin, setInvestorLinkedin] = useState<string>(
    lead.linkedin_url || '',
  );
  const [investorPhone, setInvestorPhone] = useState<string>(
    lead.contact_phone || '',
  );

  // Company pre-fill
  const [companyName, setCompanyName] = useState<string>(
    lead.company_name || lead.contact_name || '',
  );
  const [companyDescription, setCompanyDescription] = useState<string>('');

  // Engagement pre-fill
  const [engagementCompanyName, setEngagementCompanyName] = useState<string>(
    lead.company_name || '',
  );
  const [engagementContactName, setEngagementContactName] = useState<string>(
    lead.contact_name || '',
  );
  const [engagementContactEmail, setEngagementContactEmail] = useState<string>(
    lead.contact_email || '',
  );
  const responseSummary = useMemo(() => summarizeResponses(lead), [lead]);
  const [engagementNotes, setEngagementNotes] = useState<string>(responseSummary);

  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let mapping: Record<string, any> = {};
      if (targetKind === 'investor') {
        mapping = {
          full_name: investorFullName.trim() || undefined,
          firm_name: investorFirmName.trim() || undefined,
          email: investorEmail.trim() || undefined,
          phone: investorPhone.trim() || undefined,
          linkedin_url: investorLinkedin.trim() || undefined,
        };
      } else if (targetKind === 'company') {
        mapping = {
          name: companyName.trim() || undefined,
          description: companyDescription.trim() || undefined,
        };
      } else if (targetKind === 'engagement') {
        mapping = {
          company_name: engagementCompanyName.trim() || undefined,
          contact_name: engagementContactName.trim() || undefined,
          contact_email: engagementContactEmail.trim() || undefined,
          notes: engagementNotes.trim() || undefined,
        };
      }
      // Strip undefined keys so backend defaults still kick in.
      const clean: Record<string, any> = {};
      Object.keys(mapping).forEach((k) => {
        if (mapping[k] !== undefined && mapping[k] !== '') clean[k] = mapping[k];
      });
      await onConfirm(targetKind, clean);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-apple-ink">Promote Lead</h2>
            <p className="text-xs text-apple-subtext mt-1">
              Convert lead #{lead.id} into a first-class entity. Fields below
              are pre-filled from this lead's contact and responses.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Target picker */}
        <div>
          <label className="block text-xs uppercase tracking-wide text-apple-subtext mb-2">
            Promote to
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {TARGET_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTargetKind(opt.value)}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  targetKind === opt.value
                    ? 'border-apple-blue bg-blue-50'
                    : 'border-apple-divider hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-medium text-apple-ink">{opt.label}</div>
                <div className="text-xs text-apple-subtext mt-0.5">{opt.helper}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Per-target form */}
        {targetKind === 'investor' && (
          <div className="space-y-3">
            <FormField label="Full name">
              <input
                type="text"
                value={investorFullName}
                onChange={(e) => setInvestorFullName(e.target.value)}
                className="form-input"
              />
            </FormField>
            <FormField label="Firm name">
              <input
                type="text"
                value={investorFirmName}
                onChange={(e) => setInvestorFirmName(e.target.value)}
                className="form-input"
              />
            </FormField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Email">
                <input
                  type="email"
                  value={investorEmail}
                  onChange={(e) => setInvestorEmail(e.target.value)}
                  className="form-input"
                />
              </FormField>
              <FormField label="Phone">
                <input
                  type="text"
                  value={investorPhone}
                  onChange={(e) => setInvestorPhone(e.target.value)}
                  className="form-input"
                />
              </FormField>
            </div>
            <FormField label="LinkedIn URL">
              <input
                type="text"
                value={investorLinkedin}
                onChange={(e) => setInvestorLinkedin(e.target.value)}
                className="form-input"
              />
            </FormField>
          </div>
        )}

        {targetKind === 'company' && (
          <div className="space-y-3">
            <FormField label="Company name">
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="form-input"
              />
            </FormField>
            <FormField label="Description (optional)">
              <textarea
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
                rows={3}
                className="form-input"
                placeholder="Short description of the company / product."
              />
            </FormField>
          </div>
        )}

        {targetKind === 'engagement' && (
          <div className="space-y-3">
            <FormField label="Company name">
              <input
                type="text"
                value={engagementCompanyName}
                onChange={(e) => setEngagementCompanyName(e.target.value)}
                className="form-input"
              />
            </FormField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Contact name">
                <input
                  type="text"
                  value={engagementContactName}
                  onChange={(e) => setEngagementContactName(e.target.value)}
                  className="form-input"
                />
              </FormField>
              <FormField label="Contact email">
                <input
                  type="email"
                  value={engagementContactEmail}
                  onChange={(e) => setEngagementContactEmail(e.target.value)}
                  className="form-input"
                />
              </FormField>
            </div>
            <FormField label="Notes (auto-populated from responses)">
              <textarea
                value={engagementNotes}
                onChange={(e) => setEngagementNotes(e.target.value)}
                rows={6}
                className="form-input font-mono text-xs"
              />
            </FormField>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-apple-divider">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-apple-ink hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 bg-apple-blue text-white text-sm font-medium rounded-lg hover:bg-apple-blueHover disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            Promote
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <label className="block text-xs uppercase tracking-wide text-apple-subtext mb-1">
      {label}
    </label>
    {children}
  </div>
);

export default PromoteLeadModal;
